// BarberWeeklySchedule.jsx
import React, { useState, useEffect } from 'react';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';

export default function BarberWeeklySchedule({ barberId, apiUrl }) {
  const axiosPrivate = useAxiosPrivate();

  const DEFAULT_WEEKLY_SCHEDULE = {
    mon: null, tue: null, wed: null, thu: null,
    fri: null, sat: null, sun: null
  };

  const [weeklySchedule, setWeeklySchedule] = useState(DEFAULT_WEEKLY_SCHEDULE);
  const [notification, setNotification] = useState('');
  const [loading, setLoading] = useState(true);

  const dayNames = {
    mon: 'Ponedeljak', tue: 'Utorak', wed: 'Sreda', thu: 'Četvrtak',
    fri: 'Petak', sat: 'Subota', sun: 'Nedelja'
  };

  useEffect(() => {
    if (!barberId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchSchedule();
  }, [barberId]);

  const fetchSchedule = async () => {
    try {
      const response = await axiosPrivate.get(`${apiUrl}/barbers/${barberId}`);
      setWeeklySchedule(response.data.weekly_schedule || DEFAULT_WEEKLY_SCHEDULE);
    } catch (error) {
      console.error('Error fetching schedule:', error);
      setNotification('Greška pri učitavanju rasporeda');
      setWeeklySchedule(DEFAULT_WEEKLY_SCHEDULE);
      setTimeout(() => setNotification(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const addShift = (day) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: prev[day] === null 
        ? [{ start: '09:00', end: '17:00' }]
        : [...prev[day], { start: '09:00', end: '17:00' }]
    }));
  };

  const removeShift = (day, index) => {
    setWeeklySchedule(prev => {
      const newShifts = [...prev[day]];
      newShifts.splice(index, 1);
      return { ...prev, [day]: newShifts.length === 0 ? null : newShifts };
    });
  };

  const updateShift = (day, index, field, value) => {
    setWeeklySchedule(prev => {
      const newShifts = [...prev[day]];
      newShifts[index] = { ...newShifts[index], [field]: value };
      return { ...prev, [day]: newShifts };
    });
  };

  const toggleDayOff = (day) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: prev[day] === null ? [{ start: '09:00', end: '17:00' }] : null
    }));
  };

  const handleSave = async () => {
    try {
      await axiosPrivate.put(`${apiUrl}/barbers/${barberId}`, {
        weekly_schedule: weeklySchedule
      });
      setNotification('Raspored uspešno sačuvan!');
      setTimeout(() => setNotification(''), 3000);
    } catch (error) {
      console.error('Error saving schedule:', error);
      setNotification('Greška pri čuvanju rasporeda. Probaj ponovo ili zovi Darka :)');
      setTimeout(() => setNotification(''), 3000);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-4 sm:p-6 bg-neutral-900 rounded-xl">
        <p className="text-white text-center">Učitavanje...</p>
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6 bg-neutral-900 rounded-xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 sm:mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          🕐 Nedeljni Raspored
        </h2>
        <button
          onClick={handleSave}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white rounded-lg font-semibold transition touch-manipulation"
        >
          💾 Sačuvaj
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div className={`mb-4 p-3 rounded-lg text-center text-sm ${
          notification.includes('uspešno') 
            ? 'bg-green-600 text-white' 
            : 'bg-red-600 text-white'
        }`}>
          {notification}
        </div>
      )}

      {/* Days Schedule */}
      <div className="space-y-3 sm:space-y-4">
        {Object.keys(dayNames).map(day => (
          <div key={day} className="bg-neutral-800 rounded-lg p-3 sm:p-4">
            {/* Day Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3">
              <h3 className="text-base sm:text-lg font-semibold text-white">{dayNames[day]}</h3>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={() => toggleDayOff(day)}
                  className={`px-3 py-2 rounded-md text-xs sm:text-sm font-medium transition touch-manipulation ${
                    weeklySchedule[day] === null
                      ? 'bg-red-600 text-white'
                      : 'bg-neutral-700 text-gray-300 hover:bg-neutral-600 active:bg-neutral-500'
                  }`}
                >
                  {weeklySchedule[day] === null ? '❌ Neradni dan' : 'Označi kao neradni'}
                </button>
                {weeklySchedule[day] !== null && (
                  <button
                    onClick={() => addShift(day)}
                    className="flex items-center justify-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white rounded-md text-xs sm:text-sm font-medium transition touch-manipulation"
                  >
                    ➕ Dodaj smenu
                  </button>
                )}
              </div>
            </div>

            {/* Shifts */}
            {weeklySchedule[day] !== null && weeklySchedule[day].length > 0 && (
              <div className="space-y-2">
                {weeklySchedule[day].map((shift, index) => (
                  <div key={index} className="bg-neutral-700 p-3 rounded-md">
                    <div className="flex flex-col gap-2">
                      {/* Shift Label */}
                      <span className="text-gray-300 text-xs sm:text-sm font-medium">
                        Smena {index + 1}:
                      </span>
                      
                      {/* Time Inputs Row */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <input
                          type="time"
                          value={shift.start}
                          onChange={(e) => updateShift(day, index, 'start', e.target.value)}
                          className="flex-1 min-w-[100px] px-2 sm:px-3 py-2 bg-neutral-600 text-white text-sm rounded-md border border-neutral-500 focus:border-blue-500 focus:outline-none"
                        />
                        <span className="text-gray-400 text-sm">do</span>
                        <input
                          type="time"
                          value={shift.end}
                          onChange={(e) => updateShift(day, index, 'end', e.target.value)}
                          className="flex-1 min-w-[100px] px-2 sm:px-3 py-2 bg-neutral-600 text-white text-sm rounded-md border border-neutral-500 focus:border-blue-500 focus:outline-none"
                        />
                        {weeklySchedule[day].length > 1 && (
                          <button
                            onClick={() => removeShift(day, index)}
                            className="p-2 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white rounded-md transition touch-manipulation"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Break Info */}
            {weeklySchedule[day] !== null && weeklySchedule[day].length > 1 && (
              <div className="mt-2 text-xs sm:text-sm text-gray-400 italic">
                ⏸️ Pauza između smena: {weeklySchedule[day][0].end} - {weeklySchedule[day][1].start}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-neutral-800 rounded-lg">
        <h4 className="text-white font-semibold text-sm sm:text-base mb-2">ℹ️ Napomena:</h4>
        <ul className="text-gray-300 text-xs sm:text-sm space-y-1">
          <li>• Možete dodati više smena po danu za pauze (npr. 9-14, 16-19)</li>
          <li>• Pauze su automatski kreirane između smena</li>
          <li>• Označite dan kao neradni ako ne radite tog dana</li>
          <li>• Ne zaboravite da kliknete "Sačuvaj" nakon izmena</li>
        </ul>
      </div>
    </div>
  );
}